import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/nucleo/utilitarios";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrows();

    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    el.addEventListener("scroll", updateArrows, { passive: true });

    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateArrows);
    };
  }, [updateArrows]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      left: el.scrollLeft + (direction === "left" ? -200 : 200),
      behavior: "smooth",
    });
  };

  return (
    <div className="relative w-full max-w-full">
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Rolar abas para a esquerda"
          onClick={() => scroll("left")}
          className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-0.5 pr-2 bg-linear-to-r from-background via-background/80 to-transparent rounded-l-2xl transition-opacity duration-200"
        >
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-none"
      >
        <TabsPrimitive.List
          ref={ref}
          className={cn(
            "flex w-max min-w-full items-center gap-1 rounded-2xl border border-border/40 bg-muted/50 p-1 text-muted-foreground backdrop-blur-xs",
            className,
          )}
          {...props}
        />
      </div>

      {canScrollRight && (
        <button
          type="button"
          aria-label="Rolar abas para a direita"
          onClick={() => scroll("right")}
          className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-0.5 pl-2 bg-linear-to-l from-background via-background/80 to-transparent rounded-r-2xl transition-opacity duration-200"
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * Aba ativa: `bg-card` no claro (igual ao protótipo). No escuro, `--card`
 * (13% de luminosidade) é mais escuro que `--muted` (18%, fundo do próprio
 * `TabsList`) — a aba "ativa" ficava mais escura que o resto em vez de se
 * destacar, então só no escuro (`dark:`) troca para um tom translúcido de
 * `--primary`, que sempre clareia o que está por baixo. Achado comparando
 * contra captura de tela do protótipo rodando, pedido direto do dono.
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold ring-offset-background transition-all duration-200 hover:text-foreground hover:bg-background/50 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs dark:data-[state=active]:bg-primary/10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
